/* The charge-line draft and its arithmetic.
 *
 * Split from the editor component because a component file may export only
 * components (the react-refresh rule this project enforces) — the same reason
 * `toDocumentPayload` lives beside its mutation rather than beside its sheet.
 *
 * Everything here is DISPLAY arithmetic. The server recomputes each amount from
 * unit price and quantity and sums them (AD-9), and its figures are the ones
 * that reach paper. The preview still earns its place: a typist who has just
 * entered 330 × 2 wants to see 660 before submitting, and finding out at the
 * PDF is finding out too late.
 */

import { normaliseDecimal } from '@/lib/decimal'

export interface LineDraft {
  /** Stable across edits and removals, so React keeps focus in the right input
   *  while a row above is being retyped. Never sent — the wire carries no id. */
  key: string
  description: string
  note: string
  unitPrice: string
  quantity: string
}

/* The separators a Russian or Turkish keyboard produces: `1 234,56` and
   `1234.56` are the same sum. Shared with the quotes feature, so it lives in
   `lib/decimal`; re-exported here for the callers that import it from this
   module. */
export { normaliseDecimal }

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks. Six places, because
 *  a quantity is not money — detention is billed in part-days. */
function isValidNumber(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,6})?$/.test(normalised)
}

/** The row's amount, or undefined while either figure is still unusable.
 *
 *  Parsed through Number ONLY to render a preview, never to build the payload —
 *  the strings are what travel. A row mid-typing shows nothing rather than NaN. */
export function lineAmount(line: LineDraft): number | undefined {
  if (!isValidNumber(line.unitPrice) || !isValidNumber(line.quantity)) return undefined
  const price = Number(normaliseDecimal(line.unitPrice))
  const quantity = Number(normaliseDecimal(line.quantity))
  return Math.round(price * quantity * 100) / 100
}

/** The running total of the lines that are currently complete.
 *
 *  Sums the ROUNDED row amounts, matching the server: the client adds up the
 *  column that is printed, so the total has to match that rather than a more
 *  precise figure nobody can see. */
export function linesTotal(lines: LineDraft[]): number {
  return lines.reduce((sum, line) => sum + (lineAmount(line) ?? 0), 0)
}

/** A line is submittable when it names a charge and both figures are positive.
 *
 *  A zero or negative charge is either a typo or somebody reaching for a
 *  credit, and a credit note is a different document with the opposite sign
 *  convention — letting one through here would print a negative line on paper
 *  titled DEBIT NOTE. */
export function isLineComplete(line: LineDraft): boolean {
  return (
    line.description.trim() !== '' &&
    isValidNumber(line.unitPrice) &&
    isValidNumber(line.quantity) &&
    Number(normaliseDecimal(line.unitPrice)) > 0 &&
    Number(normaliseDecimal(line.quantity)) > 0
  )
}

export function emptyLine(key: string): LineDraft {
  // A quantity of one is the overwhelmingly common case, so it is not left
  // blank for the typist to fill in on every row.
  return { key, description: '', note: '', unitPrice: '', quantity: '1' }
}
